import { ReactElement, useMemo } from 'react'
import { Vector3 } from 'three'
import { Line } from '@react-three/drei'
import TextSprite from 'src/components/scene/TextSprite'
import { getLoadCategoryColor } from 'src/components/scene/loads'
import { getTextColorOnWhite } from 'src/utils/color'
import {
  ARROW_LINE_WIDTH,
  FloorplanLineLoadGroup,
  LABEL_BORDER_WIDTH,
  LABEL_SCALE,
  OUTER_LINE_WIDTH,
  groupLabels,
  groupShapes,
  shapeSegments,
} from '../utils'

interface Props {
  group: FloorplanLineLoadGroup
}

/**
 * One group of line loads with the same extent: one rectangle or trapezoid outline per distinct
 * shape (width of the point-load circle, line width of its crosshair), plus the labels of all
 * loads in the group.
 */
const FloorplanLineLoadGroupElement = ({ group }: Props): ReactElement => {
  const shapes = useMemo(() => groupShapes(group), [group])

  const lines = useMemo(
    () =>
      shapes.flatMap(shape => {
        const { outer, arrows } = shapeSegments(group, shape.offsets)
        const parts = [
          { part: 'outer', segments: outer, lineWidth: OUTER_LINE_WIDTH },
          { part: 'arrows', segments: arrows, lineWidth: ARROW_LINE_WIDTH },
        ]
        return parts
          .filter(({ segments }) => segments.length > 0)
          .map(({ part, segments, lineWidth }) => ({
            key: `${shape.key}-${part}`,
            guid: shape.load.guid,
            color: getLoadCategoryColor(shape.load.load_case.category),
            lineWidth,
            points: segments.flatMap(segment =>
              segment.map(point => [point.x, point.y, point.z] as [number, number, number]),
            ),
          }))
      }),
    [group, shapes],
  )

  const labels = useMemo(
    () =>
      groupLabels(group, shapes).map(label => {
        const loadColor = getLoadCategoryColor(label.load.load_case.category)
        return {
          ...label,
          loadColor,
          textColor: getTextColorOnWhite(loadColor),
          position: new Vector3(label.position.x, label.position.y, label.position.z),
        }
      }),
    [group, shapes],
  )

  return (
    <>
      <group position={[0, 0, 0.001]}>
        {lines.map(line => (
          <Line
            key={line.key}
            points={line.points}
            color={line.color}
            lineWidth={line.lineWidth}
            segments
            opacity={0.75}
            dashed={false}
            userData={{
              type: 'line-load',
              contextMenuActions: {
                hide: {
                  guid: line.guid,
                },
                properties: {
                  guid: line.guid,
                },
              },
            }}
          />
        ))}
      </group>

      {labels.map(label => (
        <TextSprite
          key={label.key}
          textLines={[label.text]}
          color={label.textColor}
          backgroundColor="#ffffff"
          borderColor={label.loadColor}
          borderWidth={LABEL_BORDER_WIDTH}
          position={label.position}
          anchorLocation={label.anchor}
          rotation={label.rotation}
          scale={LABEL_SCALE}
          scaleAxis="Y"
          opacity={1.0}
        />
      ))}
    </>
  )
}

export default FloorplanLineLoadGroupElement
